import * as React from 'react'
import { Controller, ControllerProps, FieldValues } from 'react-hook-form'
import { MaskedInput, MaskedInputProps } from './MaskedInput'
import { useIntl } from 'react-intl'

type SSNInputProps = Omit<MaskedInputProps, 'format'>
const _SSNInput = ({ value, placeholder, ...props }: SSNInputProps, ref: React.Ref<HTMLInputElement>) => {
  return (
    <MaskedInput
      inputRef={ref}
      format="###-##-####"
      mask="_"
      valueIsNumericString
      placeholder={placeholder || 'XXX-XX-XXXX'}
      {...props}
      value={value}
    />
  )
}
export const SSNInput = React.forwardRef(_SSNInput)

/**
 * A controlled input for Social Security Numbers. Note that custom validation rules are not supported.
 * If the SSN is required, just supply the `required` prop.
 */
type SSNInputControllerProps<T extends FieldValues> = Omit<SSNInputProps, 'name' | 'variant'> &
  Pick<ControllerProps<T>, 'name' | 'control'>
export const SSNInputController = <T extends FieldValues>({
  name,
  control,
  ...props
}: SSNInputControllerProps<T>) => {
  const intl = useIntl()
  return (
    <Controller
      render={({ field, formState: { submitCount }, fieldState: { error, isTouched } }) => {
        const isError = Boolean((isTouched || submitCount > 0) && error?.message)
        return (
          <SSNInput
            {...props}
            {...field}
            error={isError}
            helperText={(isError && error?.message) || props.helperText}
          />
        )
      }}
      name={name}
      control={control}
      rules={{
        required: props.required ? intl.formatMessage({ id: 'common.validation.required' }) : undefined,
        pattern: {
          value: /^(?:\d{9})?$/, // 9 digits or empty
          message: intl.formatMessage({ id: 'common.validation.ssn.invalid' }),
        },
      }}
    />
  )
}
